import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { IncomeCertificateSubmitPayload } from "@/components/features/notarial-requests/income-certificate-wizard";
import { CertificateInvoicePreferencesFields } from "@/components/shared/CertificateInvoicePreferencesFields";
import { CertificateWizardPricingBreakdown } from "@/components/shared/CertificateWizardPricingBreakdown";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { certificateCatalogDisplayLabel } from "@/lib/certificateCatalogDisplayLabel";
import {
  countIncomeCertificateBillableFiles,
  type CertificatePricingCatalogItem,
} from "@/services/certificatePricingCatalogService";

function SummaryRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-4">
      <span className="shrink-0 text-sm font-medium text-foreground sm:w-48">{label}</span>
      <span className="min-w-0 text-sm text-muted-foreground">{value}</span>
    </div>
  );
}

function documentTypeLabel(
  type: string,
  t: (key: string, opts?: { defaultValue?: string }) => string,
): string {
  return t(`notary.requestDetail.documentType_${type}`, { defaultValue: type });
}

export function IncomeCertificateRequestSummary({
  payload,
  totalChargeLabel,
  catalogItem,
  invoiceIncludeRut,
  onInvoiceIncludeRutChange,
  invoiceRutDigits,
  onInvoiceRutDigitsChange,
  invoiceRutLockedFromRegistration,
}: {
  payload: IncomeCertificateSubmitPayload;
  totalChargeLabel: string | null;
  catalogItem?: CertificatePricingCatalogItem;
  invoiceIncludeRut: boolean;
  onInvoiceIncludeRutChange: (value: boolean) => void;
  invoiceRutDigits: string;
  onInvoiceRutDigitsChange: (digits: string) => void;
  invoiceRutLockedFromRegistration?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const d = payload.incomeCertificateDetails;

  const activityLabel =
    d.activity === "other" && d.activityCustom
      ? d.activityCustom
      : d.activity
        ? t(`requests.user.incomeWizard.activity.${d.activity}`, { defaultValue: d.activity })
        : "—";

  const modalityLabel = d.workModality
    ? t(`requests.user.incomeWizard.modalitySummary.${d.workModality}`, {
        defaultValue: d.workModality,
      })
    : "—";

  const periodLabel = t(`requests.user.incomeWizard.period${d.periodMonths}`);
  const billableFileCount = countIncomeCertificateBillableFiles(payload.uploads);
  const certificateLabel = certificateCatalogDisplayLabel(catalogItem, t);

  const idDocumentTypeLabel = useMemo((): string | null => {
    if (payload.uploads.some((u) => u.type === "passport_document")) {
      return t("requests.user.legalDocumentPassport");
    }
    if (payload.uploads.some((u) => u.type === "legal_document_front")) {
      return t("requests.user.legalDocumentNationalId");
    }
    return null;
  }, [payload.uploads, t]);

  return (
    <div className="space-y-6">
      <div className="wizard-summary-panel p-4 sm:p-5">
        <div className="space-y-4">
        <SummaryRow
          label={t("requests.user.wizard.summaryCertificateLabel")}
          value={certificateLabel}
        />
        <SummaryRow
          label={t("requests.user.incomeCertificateSummary.fulfillment")}
          value={t(`requests.user.fulfillmentMode.option.${payload.fulfillmentMode}`, {
            defaultValue: payload.fulfillmentMode,
          })}
        />
        <SummaryRow label={t("requests.user.incomeCertificateSummary.period")} value={periodLabel} />
        <SummaryRow
          label={t("requests.user.incomeCertificateSummary.presentingEntity")}
          value={d.presentingEntity || "—"}
        />
        {d.presentingInstitution ? (
          <SummaryRow
            label={t("requests.user.incomeCertificateSummary.presentingInstitution")}
            value={d.presentingInstitution}
          />
        ) : null}
        <SummaryRow label={t("requests.user.incomeCertificateSummary.activity")} value={activityLabel} />
        <SummaryRow label={t("requests.user.incomeCertificateSummary.modality")} value={modalityLabel} />
        {idDocumentTypeLabel ? (
          <SummaryRow
            label={t("requests.user.incomeCertificateSummary.idDocumentType")}
            value={idDocumentTypeLabel}
          />
        ) : null}
        <SummaryRow
          label={t("requests.user.incomeCertificateSummary.specialFormat")}
          value={
            d.specialFormatRequested
              ? t("requests.user.incomeWizard.specialFormatYes")
              : t("requests.user.incomeWizard.specialFormatNo")
          }
        />
        {d.bpsNumber ? (
          <SummaryRow
            label={t("requests.user.incomeCertificateSummary.bpsNumber")}
            value={d.bpsNumber}
          />
        ) : null}
        </div>
      </div>

      {payload.companySnapshot?.legalName ? (
        <>
          <Separator />
          {payload.companyRutUnverified === true ? (
            <Alert variant="default" className="border-amber-500/50 bg-amber-500/10 dark:border-amber-500/40">
              <AlertTitle className="text-foreground">{t("requests.user.manualCompanySummaryNoticeTitle")}</AlertTitle>
              <AlertDescription className="text-muted-foreground">
                {t("requests.user.manualCompanySummaryNoticeBody")}
              </AlertDescription>
            </Alert>
          ) : null}
          <div className="wizard-summary-panel space-y-3 p-4 sm:p-5">
            <h3 className="text-sm font-semibold text-foreground">
              {t("requests.user.incomeCertificateReview.companySection")}
            </h3>
            <SummaryRow
              label={t("requests.user.legalEntitySummary.companyName")}
              value={payload.companySnapshot.legalName}
            />
            {payload.companySnapshot.tradeName ? (
              <SummaryRow
                label={t("requests.user.incomeCertificateSummary.companyTradeName")}
                value={payload.companySnapshot.tradeName}
              />
            ) : null}
            {payload.companySnapshot.rut ? (
              <SummaryRow label={t("requests.user.legalEntityRutLabel")} value={payload.companySnapshot.rut} />
            ) : null}
            {payload.companySnapshot.legalAddress ? (
              <SummaryRow
                label={t("requests.user.legalEntitySummary.address")}
                value={payload.companySnapshot.legalAddress}
              />
            ) : null}
            {payload.companySnapshot.postalCode ? (
              <SummaryRow
                label={t("requests.user.incomeCertificateSummary.companyPostalCode")}
                value={payload.companySnapshot.postalCode}
              />
            ) : null}
            {payload.companySnapshot.city ? (
              <SummaryRow
                label={t("requests.user.incomeCertificateSummary.companyCity")}
                value={payload.companySnapshot.city}
              />
            ) : null}
            {payload.companySnapshot.department ? (
              <SummaryRow
                label={t("requests.user.incomeCertificateSummary.companyDepartment")}
                value={payload.companySnapshot.department}
              />
            ) : null}
            {payload.companySnapshot.phone ? (
              <SummaryRow
                label={t("requests.user.incomeCertificateSummary.companyPhone")}
                value={payload.companySnapshot.phone}
              />
            ) : null}
            {payload.companySnapshot.email ? (
              <SummaryRow
                label={t("requests.user.incomeCertificateSummary.companyEmail")}
                value={payload.companySnapshot.email}
              />
            ) : null}
            {payload.companySnapshot.economicActivity ? (
              <SummaryRow
                label={t("requests.user.incomeCertificateSummary.companyEconomicActivity")}
                value={payload.companySnapshot.economicActivity}
              />
            ) : null}
            {payload.companySnapshot.fechaInicio ? (
              <SummaryRow
                label={t("requests.user.incomeCertificateSummary.companyStartDate")}
                value={payload.companySnapshot.fechaInicio}
              />
            ) : null}
          </div>
        </>
      ) : null}

      <Separator />

      <div className="wizard-summary-panel space-y-3 p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-foreground">
          {t("requests.user.incomeCertificateReview.filesTitle")}
        </h3>
        <div className="space-y-3">
          {payload.uploads.map((u, index) => (
            <SummaryRow
              key={`${u.type}-${index}-${u.file.name}`}
              label={documentTypeLabel(u.type, t)}
              value={u.file.name}
            />
          ))}
        </div>
      </div>

      {totalChargeLabel ? (
        <>
          <Separator />
          <CertificateWizardPricingBreakdown
            catalogItem={catalogItem}
            variant="per_file"
            billableFileCount={billableFileCount}
            totalChargeLabel={totalChargeLabel}
            fulfillmentMode={payload.fulfillmentMode}
          />
        </>
      ) : null}

      <Separator />

      <CertificateInvoicePreferencesFields
        includeRut={invoiceIncludeRut}
        onIncludeRutChange={onInvoiceIncludeRutChange}
        rutDigits={invoiceRutDigits}
        onRutDigitsChange={onInvoiceRutDigitsChange}
        rutLockedFromRegistration={invoiceRutLockedFromRegistration === true}
      />
    </div>
  );
}
